// Relatórios (E8): a lista; o dos caminhões (km, consumo, custo por km e
// quanto sobrou); o demonstrativo de remuneração por pessoa; e o do contador
// (resumo em PDF e as fotos das notas num .zip). Calculados no aparelho com o
// que já está nele: funcionam sem sinal (só as fotos do .zip que não estão no
// aparelho precisam de internet).
import { anexos as anexosDados, dadosDosRelatorios } from '../dados.js';
import { agenciamentosPendentes, creditosDaViagem, regraValendo } from '../lib/acerto.js';
import { REGIMES, anexosDoRelatorio, relatorioDoContador } from '../lib/contador.js';
import { RATEIOS, mesesEntre, relatorioDosCaminhoes, ultimoDiaDoMes } from '../lib/custos.js';
import { h, trocar } from '../lib/dom.js';
import { mensagemDeErro } from '../lib/erros.js';
import { data, dinheiro, placa as formatarPlaca, mesCurto, numeroBR, plural } from '../lib/formato.js';
import { ORDEM_CREDITOS, ORDEM_DESCONTOS, ganhosDaPessoa, temGanhos } from '../lib/ganhos.js';
import { montarZip, nomeDoZipDoContador, nomesNoPacote } from '../lib/pacote.js';
import { caminhoesEmPdf, nomeDoArquivoDosCaminhoes, notaDoMes, rotuloDoTrecho } from '../lib/pdfcaminhoes.js';
import { contadorEmPdf, nomeDoArquivoDoContador } from '../lib/pdfcontador.js';
import { NOMES_DOS_DESCONTOS, NOMES_NO_DEMONSTRATIVO, ganhosEmPdf, nomeDaPessoa, nomeDoArquivoDosGanhos } from '../lib/pdfganhos.js';
import { entregarArquivo } from '../lib/recibo.js';
import { aoChegarDados } from '../local/sincronia.js';
import { caminhoAtual } from '../rotas.js';
import { hojeDaEmpresa, sessao } from '../sessao.js';
import { casca } from '../ui/casca.js';
import { avisar, campo, carregando, cartaoLink, faixa, secao, trocarOpcoes } from '../ui/componentes.js';

const contas = { regraValendo, creditosDaViagem, agenciamentosPendentes };

// Rateio das despesas gerais usado no custo de cada caminhão.
export const RATEIO_PADRAO = 'km';

export function telaRelatorios() {
  return casca({
    titulo: 'Relatórios',
    voltar: '/mais',
    conteudo: h('div', { class: 'secao' },
      h('p', { class: 'pequeno' }, 'Feitos no aparelho, com o que já está nele: funcionam sem sinal.'),
      h('div', { class: 'lista' },
        cartaoLink({ href: '#/relatorios/semana', simbolo: 'calendario', titulo: 'Resumo da semana', linhas: ['Os últimos 7 dias e o que pede atenção, para mandar pelo WhatsApp'] }),
        cartaoLink({ href: '#/relatorios/caminhoes', simbolo: 'caminhao', titulo: 'Caminhões', linhas: ['Km rodados, consumo, custo por km e quanto sobrou, mês a mês'] }),
        cartaoLink({ href: '#/relatorios/remuneracao', simbolo: 'pessoas', titulo: 'Remuneração por pessoa', linhas: ['O que cada um ganhou, mês a mês (serve de comprovante de renda)'] }),
        cartaoLink({ href: '#/relatorios/contador', simbolo: 'recibo', titulo: 'Para o contador', linhas: ['Fretes com as retenções, despesas por categoria e as fotos das notas'] }),
        cartaoLink({ href: '#/acertos/relatorio', simbolo: 'grafico', titulo: 'Acertos entre datas', linhas: ['Os acertos fechados no período, com os demonstrativos'] }))),
  });
}

// ---------------------------------------------------------------------
// Caminhões
// ---------------------------------------------------------------------
const kmTexto = (n) => `${numeroBR(Math.round(n))} km`;
// "2026-09" → "set/26" (para caber na tabela do celular)
const mesNaTabela = (mes) => `${mesCurto(mes).slice(0, 4)}${mes.slice(2, 4)}`;
const porKm = (v) => `${dinheiro(v)}/km`;
const kmPorLitro = (c) => `${numeroBR(c.kmPorLitro, { casas: 2 })} km/l`;

function mesAntes(mes, n) {
  const [a, m] = mes.split('-').map(Number);
  const d = new Date(Date.UTC(a, m - 1 - n, 1));
  return d.toISOString().slice(0, 7);
}

// Os períodos prontos, a partir do mês de hoje.
function periodosProntos(mesAtual) {
  const ano = mesAtual.slice(0, 4);
  return [
    ['este-mes', `Este mês (${mesCurto(mesAtual)})`, mesAtual, mesAtual],
    ['mes-passado', `Mês passado (${mesCurto(mesAntes(mesAtual, 1))})`, mesAntes(mesAtual, 1), mesAntes(mesAtual, 1)],
    ['3-meses', 'Últimos 3 meses', mesAntes(mesAtual, 2), mesAtual],
    ['6-meses', 'Últimos 6 meses', mesAntes(mesAtual, 5), mesAtual],
    ['12-meses', 'Últimos 12 meses', mesAntes(mesAtual, 11), mesAtual],
    ['este-ano', `Este ano (${ano})`, `${ano}-01`, mesAtual],
    ['ano-passado', `Ano passado (${Number(ano) - 1})`, `${Number(ano) - 1}-01`, `${Number(ano) - 1}-12`],
  ];
}

// [chave, rótulo, explicação, a linha dos lançamentos (lib/custos.js)]
const GRUPOS = [
  ['outrosDaViagem', 'Outros gastos da viagem', 'pedágio, alimentação, chapa…', 'viagem'],
  ['manutencao', 'Manutenção e pneus', null, 'manutencao'],
  ['fixo', 'Fixos', 'seguro, IPVA, financiamento…', 'fixo'],
  ['outros', 'Outras despesas', null, 'outros'],
];

// O que explica os km de um caminhão no período.
function notasDoKm(c) {
  const notas = [];
  if (c.km.semLeitura) notas.push('Sem leitura de km no período: lance o km do painel nos abastecimentos.');
  else if (c.km.dentroDoPeriodo) {
    notas.push(`Km contados da primeira leitura do período (${data(c.km.desde)}): a leitura anterior é de ${data(c.km.ultimaAntes)}.`);
  } else if (c.km.desdeAPrimeira) notas.push(`Km contados da primeira leitura (${data(c.km.desde)}).`);
  const semLeitura = c.meses.filter((m) => m.km.semLeitura && (m.custo !== 0 || m.cargas > 0)).map((m) => mesCurto(m.mes));
  if (semLeitura.length > 0 && !c.km.semLeitura) notas.push(`Sem leitura de km em ${semLeitura.join(', ')}.`);
  return notas;
}

// "9 parcelas em aberto em 31/10/2026: entram nos custos quando forem pagas"
function textoDoEmAberto({ parcelas }, ate) {
  return parcelas === 1
    ? `1 parcela em aberto em ${data(ate)}: entra nos custos quando for paga`
    : `${parcelas} parcelas em aberto em ${data(ate)}: entram nos custos quando forem pagas`;
}

// Uma linha "rótulo ... valor" (com uma explicação pequena embaixo).
function linha(rotulo, valor, { classe = '', dica = null } = {}) {
  return h('div', { class: `linha-valor${classe ? ` ${classe}` : ''}` },
    h('span', null, rotulo, dica && h('small', null, dica)),
    h('span', null, dinheiro(valor)));
}

// Os lançamentos de uma linha do relatório (pedido do titular em 13/10/2026:
// cada valor discriminado, para conferir).
const itensDa = (c, chave) => (c.itens ?? []).filter((i) => i.linha === chave);

// A linha com os lançamentos dela embaixo (toque para ver).
function linhaComItens(rotulo, valor, itens, { classe = '', dica = null } = {}) {
  if (!itens || itens.length === 0) return linha(rotulo, valor, { classe, dica });
  return h('details', { class: 'linha-detalhada' },
    h('summary', { class: `linha-valor${classe ? ` ${classe}` : ''}` },
      h('span', null, rotulo, dica && h('small', null, dica),
        h('small', { class: 'ver-itens' }, itens.length === 1 ? 'ver o lançamento' : `ver os ${itens.length} lançamentos`)),
      h('span', null, dinheiro(valor))),
    h('ul', { class: 'itens-da-linha' }, itens.map((i) => h('li', null,
      h('span', null, h('strong', null, `${data(i.data)} · ${i.nome}`), i.detalhe && h('small', null, i.detalhe)),
      h('span', null, dinheiro(i.valor))))));
}

function blocoCaminhao(c, { comResultado, ate }) {
  const g = c.porGrupo;
  const centavos = (v) => Math.round((v ?? 0) * 100);
  const valores = {
    outrosDaViagem: (centavos(g.viagem) - centavos(c.diesel)) / 100,
    manutencao: g.manutencao ?? 0,
    fixo: g.fixo ?? 0,
    // As outras: o que não é da viagem, da manutenção nem fixo
    outros: (centavos(c.despesas) - centavos(g.viagem) - centavos(g.manutencao) - centavos(g.fixo)) / 100,
  };
  const prevista = c.remuneracao.prevista;
  const titulo = `${formatarPlaca(c.veiculo.placa ?? '')}${c.veiculo.apelido ? ` · ${c.veiculo.apelido}` : ''}`;
  const meses = c.meses.filter((m) => m.custo !== 0 || m.cargas > 0 || m.km.km !== null);

  return secao(titulo, h('div', { class: 'lista' },
    h('div', { class: 'cartao destaque' }, h('div', { class: 'cartao-corpo' },
      h('span', { class: 'cartao-titulo' }, 'Custo por km'),
      h('span', { class: 'valor-grande' }, c.custoPorKm !== null ? porKm(c.custoPorKm) : '—'),
      h('span', { class: 'cartao-linha' },
        c.km.km !== null ? `${kmTexto(c.km.km)} rodados` : 'Km rodados: sem leitura',
        c.consumo ? ` · consumo ${kmPorLitro(c.consumo)}` : ''))),
    h('div', { class: 'grupo-valores' },
      h('h3', null, 'Custos'),
      c.diesel !== 0 && linhaComItens('Diesel', c.diesel, itensDa(c, 'diesel')),
      GRUPOS.filter(([k]) => valores[k] !== 0).map(([k, rotulo, dica, chave]) => linhaComItens(rotulo, valores[k], itensDa(c, chave), { dica })),
      c.gerais !== 0 && linhaComItens('Parte das despesas gerais', c.gerais, itensDa(c, 'gerais')),
      c.remuneracao.total !== 0 && linhaComItens('Motorista', c.remuneracao.total, itensDa(c, 'motorista'),
        { dica: prevista !== 0 ? `${dinheiro(prevista)} pelas regras, ainda não acertado` : null }),
      c.agenciamentos !== 0 && linhaComItens('Agenciamentos', c.agenciamentos, itensDa(c, 'agenciamento'), { dica: 'das cargas, à agenciadora ou à transportadora' }),
      linha('= Custos', c.custo, { classe: 'total' })),
    comResultado && h('div', { class: 'grupo-valores' },
      h('h3', null, 'Resultado'),
      linhaComItens(`Fretes (${plural(c.cargas, 'carga')})`, c.fretes, itensDa(c, 'frete')),
      c.estadias !== 0 && linhaComItens('+ Estadias', c.estadias, itensDa(c, 'estadia')),
      c.descontos !== 0 && linhaComItens('− Descontos do contratante', c.descontos, itensDa(c, 'desconto'), { dica: 'INSS, SEST/SENAT, IRRF…' }),
      linha('− Custos', c.custo),
      linha('= Sobrou', c.sobrou, { classe: `total${c.sobrou < 0 ? ' perigo' : ''}` })),
    c.emAberto && c.emAberto.valor !== 0 && h('div', { class: 'grupo-valores' },
      linhaComItens('Ainda a pagar', c.emAberto.valor, c.emAberto.itens ?? [], { dica: textoDoEmAberto(c.emAberto, ate) })),
    notasDoKm(c).map((n) => h('p', { class: 'pequeno' }, n)),
    meses.length > 1 && h('div', { class: 'tabela-rolagem' }, h('table', { class: 'tabela-meses' },
      h('thead', null, h('tr', null,
        h('th', { scope: 'col' }, 'Mês'), h('th', { scope: 'col' }, 'Km'), h('th', { scope: 'col' }, 'km/l'),
        h('th', { scope: 'col' }, 'R$/km'), comResultado && h('th', { scope: 'col' }, 'Sobrou (R$)'))),
      h('tbody', null, meses.map((m) => h('tr', null,
        h('th', { scope: 'row' }, m.inteiro === false ? rotuloDoTrecho(m) : mesNaTabela(m.mes)),
        h('td', null, m.km.km === null ? '—' : `${numeroBR(m.km.km)}${m.km.acumulado || m.km.desdeAPrimeira ? '*' : ''}`),
        h('td', null, m.consumo ? numeroBR(m.consumo.kmPorLitro, { casas: 2 }) : '—'),
        h('td', null, m.custoPorKm !== null ? numeroBR(m.custoPorKm, { casas: 2 }) : '—'),
        comResultado && h('td', { class: m.sobrou < 0 ? 'perigo' : null }, numeroBR(Math.round(m.sobrou)))))))),
    meses.filter((m) => m.km.acumulado || m.km.desdeAPrimeira).map((m) => h('p', { class: 'pequeno' }, notaDoMes(m)))));
}

function blocoEmpresa(r, { comResultado }) {
  const soma = (campo) => r.caminhoes.reduce((t, c) => t + Math.round(c[campo] * 100), 0) / 100;
  const custos = soma('custo');
  const fora = r.gerais.semRateio;
  const total = Math.round((custos + fora + r.impostos) * 100) / 100;
  const sobrou = Math.round((soma('sobrou') - fora - r.impostos) * 100) / 100;
  return secao('Toda a frota', h('div', { class: 'grupo-valores' },
    comResultado && linha('Fretes e estadias', soma('fretes') + soma('estadias')),
    comResultado && soma('descontos') !== 0 && linha('− Descontos do contratante', soma('descontos')),
    linha(comResultado ? '− Custos dos caminhões' : 'Custos dos caminhões', custos),
    fora !== 0 && linhaComItens(comResultado ? '− Despesas gerais fora dos caminhões' : 'Despesas gerais fora dos caminhões', fora, r.gerais.itens ?? []),
    r.impostos !== 0 && linhaComItens(comResultado ? '− Impostos e taxas' : 'Impostos e taxas', r.impostos, r.itensImpostos ?? []),
    comResultado
      ? linha('= Sobrou', sobrou, { classe: `total${sobrou < 0 ? ' perigo' : ''}` })
      : linha('= Total', total, { classe: 'total' }),
    r.emAbertoSemCaminhao && (() => {
      const aberto = r.caminhoes.reduce((t, c) => t + Math.round((c.emAberto?.valor ?? 0) * 100), 0) + Math.round(r.emAbertoSemCaminhao.valor * 100);
      return aberto !== 0 && linhaComItens('Ainda a pagar (parcelas em aberto)', aberto / 100, r.emAbertoSemCaminhao.itens ?? [],
        { dica: `em ${data(r.ate)}, sem caminhão (as dos caminhões estão em cada um); entram nos custos quando forem pagas` });
    })()));
}

// ---------------------------------------------------------------------
// Moldura dos relatórios: o período (pronto ou escolhido, guardado no
// endereço da tela), os campos de cada relatório, as contas refeitas quando
// chegam dados do servidor e os botões de baixar.
//   extras:  { inicial(busca, dados), campos(filtro, dados, mudou), ler(form), endereco(filtro) }
//   montar(dados, filtro, { de, ate, hoje }) → { conteudo, vazio, resultado }
//   botoes:  [{ texto, gerar(resultado, filtro, progresso) → { blob, nome, titulo, aviso } }]
// ---------------------------------------------------------------------
//   datas: o período também pode ser entre datas (dia a dia); `montar` recebe
//   { dias: true } e de/até em dias ('AAAA-MM-DD'), em vez de meses.
function telaDeRelatorio({ rota, titulo, busca, explicacao = null, padrao = '6-meses', datas = false, extras = null, montar, botoes }) {
  // A tela não é redesenhada inteira quando chegam dados: refaz só as contas.
  const corpo = h('div', { class: 'secao', 'data-atualiza-sozinha': '' }, carregando());

  (async () => {
    let dados = await dadosDosRelatorios();
    // O filtro do endereço de agora: se a pessoa mexeu nele enquanto esta
    // tela carregava (ela pode estar sendo redesenhada), vale o mais novo.
    if (caminhoAtual().startsWith(rota)) busca = new URLSearchParams(caminhoAtual().split('?')[1] ?? '');
    const hoje = hojeDaEmpresa();
    const mesAtual = hoje.slice(0, 7);
    const prontos = periodosProntos(mesAtual);
    // Meses que dá para escolher: do primeiro lançamento (ou carga) até hoje.
    const mesesDos = (d) => {
      const datas = [...d.lancamentos.map((l) => l.data), ...d.fretes.map((f) => f.data_carregamento)].filter(Boolean).sort();
      return mesesEntre(datas[0] && datas[0] < hoje ? datas[0] : hoje, hoje).reverse();
    };
    let meses = mesesDos(dados);
    const mesValido = (m) => /^\d{4}-\d{2}$/.test(m ?? '') && m <= mesAtual;
    const diaValido = (d) => /^\d{4}-\d{2}-\d{2}$/.test(d ?? '');
    const pedido = busca.get('periodo');
    let filtro = {
      periodo: prontos.some(([k]) => k === pedido) || pedido === 'escolher' || (datas && pedido === 'datas') ? pedido : padrao,
      de: mesValido(busca.get('de')) ? busca.get('de') : mesAntes(mesAtual, 5),
      ate: mesValido(busca.get('ate')) ? busca.get('ate') : mesAtual,
      de_dia: diaValido(busca.get('de_dia')) ? busca.get('de_dia') : `${mesAtual}-01`,
      ate_dia: diaValido(busca.get('ate_dia')) ? busca.get('ate_dia') : hoje,
      ...(extras?.inicial(busca, dados) ?? {}),
    };

    const resultado = h('div', { 'aria-live': 'polite' });
    const erro = h('p', { class: 'erro-campo', role: 'alert' });
    let ultimo = null;
    const botoesNaTela = botoes.map((b, i) => h('button', {
      class: `botao cheio${i > 0 ? ' secundario' : ''}`, type: 'button', onClick: (e) => baixar(b, e.currentTarget),
    }, b.texto));
    const escolher = h('div', { class: 'grade-2' },
      campo({ rotulo: 'De', nome: 'de', tipo: 'select', valor: filtro.de, opcoes: meses.map((m) => [m, mesCurto(m)]), aoMudar: () => mudou() }),
      campo({ rotulo: 'Até', nome: 'ate', tipo: 'select', valor: filtro.ate, opcoes: meses.map((m) => [m, mesCurto(m)]), aoMudar: () => mudou() }));
    const escolherDias = datas && h('div', { class: 'grade-2' },
      campo({ rotulo: 'Do dia', nome: 'de_dia', tipo: 'date', valor: filtro.de_dia, aoMudar: () => mudou() }),
      campo({ rotulo: 'Até o dia', nome: 'ate_dia', tipo: 'date', valor: filtro.ate_dia, aoMudar: () => mudou() }));

    const form = h('form', { novalidate: true, onSubmit: (e) => e.preventDefault() },
      extras?.campos(filtro, dados, () => mudou()),
      campo({
        rotulo: 'Período',
        nome: 'periodo',
        tipo: 'select',
        valor: filtro.periodo,
        opcoes: [...prontos.map(([k, rotulo]) => [k, rotulo]), ['escolher', 'Escolher os meses…'], ...(datas ? [['datas', 'Escolher as datas…']] : [])],
        aoMudar: () => mudou(),
      }),
      escolher,
      escolherDias,
      erro);

    function lerFiltro() {
      const valor = (nome) => form.querySelector(`[name="${nome}"]`)?.value ?? '';
      return {
        ...filtro, periodo: valor('periodo'), de: valor('de'), ate: valor('ate'), de_dia: valor('de_dia'), ate_dia: valor('ate_dia'),
        ...(extras?.ler(form) ?? {}),
      };
    }

    function intervalo(f) {
      if (f.periodo === 'datas') return { de: f.de_dia, ate: f.ate_dia, dias: true };
      if (f.periodo === 'escolher') return { de: f.de, ate: f.ate };
      const [, , de, ate] = prontos.find(([k]) => k === f.periodo) ?? prontos.find(([k]) => k === padrao);
      return { de, ate };
    }

    // O filtro fica no endereço: se a tela for redesenhada (chegaram dados do
    // servidor), ele continua o mesmo.
    function mudou() {
      filtro = lerFiltro();
      const q = new URLSearchParams({ ...(extras?.endereco(filtro) ?? {}), periodo: filtro.periodo });
      if (filtro.periodo === 'escolher') {
        q.set('de', filtro.de);
        q.set('ate', filtro.ate);
      }
      if (filtro.periodo === 'datas') {
        q.set('de_dia', filtro.de_dia);
        q.set('ate_dia', filtro.ate_dia);
      }
      history.replaceState(null, '', `#${rota}?${q}`);
      desenhar();
    }

    function desenhar() {
      escolher.hidden = filtro.periodo !== 'escolher';
      if (escolherDias) escolherDias.hidden = filtro.periodo !== 'datas';
      const { de, ate, dias = false } = intervalo(filtro);
      const semData = dias && (!de || !ate);
      erro.textContent = semData ? 'Escolha as duas datas.'
        : de > ate ? (dias ? 'A data inicial precisa ser antes da final.' : 'O mês inicial precisa ser antes do final.') : '';
      if (semData || de > ate) {
        trocar(resultado);
        for (const b of botoesNaTela) b.disabled = true;
        return;
      }
      const r = montar(dados, filtro, { de, ate, hoje, dias });
      ultimo = r.resultado;
      for (const b of botoesNaTela) b.disabled = r.vazio;
      trocar(resultado, r.conteudo);
    }

    async function baixar(b, botao) {
      if (!ultimo) return;
      for (const x of botoesNaTela) x.disabled = true;
      const antes = botao.textContent;
      botao.textContent = 'Gerando…';
      try {
        const r = await b.gerar(ultimo, filtro, (t) => {
          botao.textContent = t;
        });
        const resposta = await entregarArquivo(r.blob, r.nome, r.titulo);
        if (r.aviso) avisar(r.aviso, r.avisoTipo ?? 'ok');
        else if (resposta === 'baixado') avisar('Arquivo baixado.');
      } catch (e) {
        console.error(e);
        avisar(mensagemDeErro(e) || 'Não foi possível gerar o arquivo agora. Tente de novo.', 'erro');
      } finally {
        if (botao.isConnected) {
          for (const x of botoesNaTela) x.disabled = false;
          botao.textContent = antes;
        }
      }
    }

    // Chegaram dados do servidor com a pessoa mexendo no filtro (a tela não é
    // redesenhada inteira): refaz só as contas.
    let vista = false;
    const desligar = aoChegarDados(async () => {
      if (!corpo.isConnected) {
        if (vista || !caminhoAtual().startsWith(rota)) desligar();
        return;
      }
      vista = true;
      dados = await dadosDosRelatorios();
      // Chegaram meses mais antigos (a primeira vez no aparelho): as opções crescem.
      const novos = mesesDos(dados);
      if (novos.join() !== meses.join()) {
        meses = novos;
        for (const nome of ['de', 'ate']) {
          trocarOpcoes(form.querySelector(`[name="${nome}"]`), meses.map((m) => [m, mesCurto(m)]), filtro[nome]);
        }
      }
      desenhar();
    });

    trocar(corpo,
      explicacao && h('p', { class: 'pequeno' }, explicacao),
      form,
      h('div', { class: 'acoes' }, botoesNaTela),
      resultado);
    desenhar();
  })().catch((e) => {
    console.error(e);
    trocar(corpo, faixa(mensagemDeErro(e), 'perigo'));
  });

  return casca({ titulo, voltar: '/relatorios', conteudo: corpo });
}

const tituloDoPeriodo = (de, ate) => (de === ate ? mesCurto(de) : `${mesCurto(de)} a ${mesCurto(ate)}`);
// Entre datas: "05/10/2026 a 20/10/2026"
const tituloDasDatas = (de, ate) => (de === ate ? data(de) : `${data(de)} a ${data(ate)}`);

// ---------------------------------------------------------------------
// Caminhões
// ---------------------------------------------------------------------
// Os caminhões (cavalos) para escolher um só, pela placa.
const tratoresDos = (dados) => (dados.veiculos ?? []).filter((v) => !v.deleted_at && v.tipo === 'trator')
  .sort((a, b) => String(a.placa ?? '').localeCompare(String(b.placa ?? '')));
const nomeDoCaminhao = (v) => `${formatarPlaca(v.placa ?? '')}${v.apelido ? ` · ${v.apelido}` : ''}`;

export function telaRelatorioCaminhoes({ busca }) {
  const rateioPedido = busca.get('rateio');
  const rateio = Object.hasOwn(RATEIOS, rateioPedido ?? '') ? rateioPedido : RATEIO_PADRAO;
  const comResultado = busca.get('resultado') !== 'nao';
  return telaDeRelatorio({
    rota: '/relatorios/caminhoes',
    titulo: 'Caminhões',
    busca,
    datas: true,
    explicacao: 'Custo por km = (despesas do caminhão e da carreta + parte das despesas gerais + motorista + agenciamentos) ÷ km rodados. '
      + 'Km rodados: a maior leitura do mês menos a maior até o mês anterior (abastecimentos, despesas e viagens).',
    // Um caminhão só (ou todos) e como contar as despesas (pedido do titular
    // em 13/10/2026): pelo que foi pago (a prazo, só as parcelas pagas) ou pela
    // data da compra.
    extras: {
      inicial: (b, dados) => ({
        caminhao: tratoresDos(dados).some((v) => v.id === b.get('caminhao')) ? b.get('caminhao') : '',
        despesas: b.get('despesas') === 'lancamento' ? 'lancamento' : 'pagamento',
      }),
      campos: (filtro, dados, mudou) => [
        campo({
          rotulo: 'Caminhão', nome: 'caminhao', tipo: 'select', valor: filtro.caminhao,
          opcoes: [['', 'Todos os caminhões'], ...tratoresDos(dados).map((v) => [v.id, nomeDoCaminhao(v)])], aoMudar: mudou,
        }),
        campo({
          rotulo: 'Contar as despesas', nome: 'despesas', tipo: 'segmentos', valor: filtro.despesas,
          opcoes: [
            ['pagamento', 'Pelo que foi pago', 'a prazo: só as parcelas já pagas'],
            ['lancamento', 'Pela data da compra', 'a compra a prazo inteira, no dia dela'],
          ],
          aoMudar: mudou,
        }),
      ],
      ler: (form) => ({
        caminhao: form.querySelector('[name="caminhao"]')?.value ?? '',
        despesas: form.querySelector('[name="despesas"]:checked')?.value ?? 'pagamento',
      }),
      endereco: (filtro) => ({
        ...(filtro.caminhao ? { caminhao: filtro.caminhao } : {}),
        despesas: filtro.despesas,
        ...(rateio !== RATEIO_PADRAO ? { rateio } : {}),
        ...(comResultado ? {} : { resultado: 'nao' }),
      }),
    },
    montar: (dados, filtro, { de, ate, hoje, dias }) => {
      const periodo = dias ? { de, ate } : { de: `${de}-01`, ate: ultimoDiaDoMes(ate) };
      const r = relatorioDosCaminhoes(dados, {
        ...periodo, hoje, rateio, despesas: filtro.despesas, veiculoId: filtro.caminhao || null, regraValendo, creditosDaViagem,
      });
      const titulo = dias ? tituloDasDatas(de, ate) : tituloDoPeriodo(de, ate);
      const como = r.despesas === 'pagamento'
        ? 'Despesas pelo que foi pago (à vista na data dela; a prazo, só as parcelas pagas no período); fretes e motorista pela data da carga'
        : 'Custos pela data da despesa e da carga';
      return {
        resultado: r,
        vazio: r.caminhoes.length === 0,
        conteudo: r.caminhoes.length === 0
          ? faixa(`Nenhum caminhão com km, despesa ou carga em ${titulo}.`, 'info')
          : [
            h('p', { class: 'pequeno' }, `${titulo}. ${como}; despesas gerais ${RATEIOS[r.rateio]}.`),
            r.caminhoes.map((c) => blocoCaminhao(c, { comResultado, ate: r.ate })),
            r.caminhoes.length > 1 && blocoEmpresa(r, { comResultado }),
          ],
      };
    },
    botoes: [{
      texto: 'Baixar o relatório em PDF',
      gerar: async (r) => ({
        blob: await caminhoesEmPdf({ relatorio: r, empresa: sessao.empresa, comResultado }),
        nome: nomeDoArquivoDosCaminhoes(r),
        titulo: 'Relatório dos caminhões',
      }),
    }],
  });
}

// ---------------------------------------------------------------------
// Remuneração por pessoa
// ---------------------------------------------------------------------
function rotuloDaPessoa(c) {
  return `${nomeDaPessoa(c)}${c.ativo === false ? ' · desativado' : ''}`;
}

function blocoGanhos(c, g) {
  const meses = g.meses.filter((m) => m.totalCreditos !== 0 || m.totalDescontos !== 0);
  return secao(rotuloDaPessoa(c), h('div', { class: 'lista' },
    h('div', { class: 'cartao destaque' }, h('div', { class: 'cartao-corpo' },
      h('span', { class: 'cartao-titulo' }, 'Ganhou no período'),
      h('span', { class: 'valor-grande' }, dinheiro(g.totais.ganho)),
      h('span', { class: 'cartao-linha' }, `Recebeu ${dinheiro(g.recebido.total)} em vales e acertos.`))),
    h('div', { class: 'grupo-valores' },
      h('h3', null, 'O que ganhou'),
      ORDEM_CREDITOS.filter((k) => g.totais.creditos[k]).map((k) => linha(NOMES_NO_DEMONSTRATIVO[k], g.totais.creditos[k])),
      g.totais.folha !== 0 && linha('Fixo pago pela folha', g.totais.folha, { dica: 'pelo cadastro' }),
      ORDEM_DESCONTOS.filter((k) => g.totais.descontos[k]).map((k) => linha(`− ${NOMES_DOS_DESCONTOS[k]}`, -g.totais.descontos[k])),
      linha('= Total ganho', g.totais.ganho, { classe: 'total' })),
    meses.length > 1 && h('div', { class: 'tabela-rolagem' }, h('table', { class: 'tabela-meses' },
      h('thead', null, h('tr', null,
        h('th', { scope: 'col' }, 'Mês'), h('th', { scope: 'col' }, 'Créditos'), h('th', { scope: 'col' }, 'Descontos'), h('th', { scope: 'col' }, 'Ganho'))),
      h('tbody', null, meses.map((m) => h('tr', null,
        h('th', { scope: 'row' }, mesNaTabela(m.mes)),
        h('td', null, numeroBR(m.totalCreditos, { casas: 2 })),
        h('td', null, m.totalDescontos ? numeroBR(-m.totalDescontos, { casas: 2 }) : '—'),
        h('td', null, numeroBR(m.ganho, { casas: 2 }))))))),
    g.recebido.total !== 0 && h('div', { class: 'grupo-valores' },
      h('h3', null, 'O que recebeu'),
      g.recebido.vales !== 0 && linha('Vales (adiantamentos)', g.recebido.vales),
      g.recebido.pagamentos !== 0 && linha('Pagamentos dos acertos', g.recebido.pagamentos),
      g.recebido.devolucoes !== 0 && linha('− Devolvido à empresa', -g.recebido.devolucoes),
      g.recebido.despesas !== 0 && linha('− Reembolso das despesas que pagou', -g.recebido.despesas, { dica: 'diesel, pedágio… (o acerto devolve)' }),
      linha(g.recebido.despesas !== 0 ? '= Recebido pela remuneração' : '= Total recebido', g.recebido.liquido, { classe: 'total' }),
      g.historico && h('p', { class: 'pequeno' }, 'Parte do período veio dos programas antigos, que não registravam os pagamentos das comissões: no recebido aparecem só os vales.')),
    g.aAcertar.itens.length > 0 && h('div', { class: 'grupo-valores' },
      h('h3', null, 'Ainda não acertado'),
      g.aAcertar.itens.map((x) => linha(`${data(x.data)} · ${x.descricao || NOMES_NO_DEMONSTRATIVO[x.subtipo] || 'Crédito'}`, x.valor, { dica: x.lancado ? null : 'pelas regras' })),
      linha('= Total a acertar', g.aAcertar.total, { classe: 'total' })),
    g.implantacao.map((s) => h('p', { class: 'pequeno' },
      `Saldo trazido dos programas antigos em ${data(s.data)}: ${dinheiro(s.valor)}. Não entra no total ganho: é o que estava em aberto na troca de programa.`))));
}

export function telaRelatorioRemuneracao({ busca }) {
  return telaDeRelatorio({
    rota: '/relatorios/remuneracao',
    titulo: 'Remuneração por pessoa',
    busca,
    explicacao: 'O que cada pessoa ganhou nos acertos fechados (e no histórico dos programas antigos), mês a mês, com o fixo pago pela folha. '
      + 'O PDF tem lugar para a assinatura da empresa e serve de comprovante de renda.',
    extras: {
      inicial: (b, dados) => {
        const pessoas = dados.colaboradores;
        const pedida = b.get('pessoa');
        const padrao = pessoas.find((p) => p.ativo !== false && p.funcao === 'motorista') ?? pessoas[0];
        return { pessoa: pedida === 'todas' || pessoas.some((p) => p.id === pedida) ? pedida : padrao?.id ?? 'todas' };
      },
      campos: (filtro, dados, mudou) => campo({
        rotulo: 'Pessoa',
        nome: 'pessoa',
        tipo: 'select',
        valor: filtro.pessoa,
        opcoes: [...dados.colaboradores.map((p) => [p.id, rotuloDaPessoa(p)]), ['todas', 'Todas as pessoas']],
        aoMudar: mudou,
      }),
      ler: (form) => ({ pessoa: form.querySelector('[name="pessoa"]')?.value ?? 'todas' }),
      endereco: (filtro) => ({ pessoa: filtro.pessoa }),
    },
    montar: (dados, filtro, { de, ate, hoje }) => {
      const periodo = { de: `${de}-01`, ate: ultimoDiaDoMes(ate), hoje };
      const escolhidas = filtro.pessoa === 'todas' ? dados.colaboradores : dados.colaboradores.filter((p) => p.id === filtro.pessoa);
      const pessoas = escolhidas
        .map((colaborador) => ({ colaborador, ganhos: ganhosDaPessoa(colaborador.id, dados, periodo, contas) }))
        .filter((x) => filtro.pessoa !== 'todas' || temGanhos(x.ganhos));
      const titulo = tituloDoPeriodo(de, ate);
      const nada = pessoas.length === 0 || pessoas.every((x) => !temGanhos(x.ganhos));
      return {
        resultado: { pessoas, de: periodo.de, ate: periodo.ate, pessoa: filtro.pessoa },
        vazio: nada,
        conteudo: nada
          ? faixa(`Nada ganho nem recebido em ${titulo}${filtro.pessoa === 'todas' ? '' : ' por esta pessoa'}.`, 'info')
          : [h('p', { class: 'pequeno' }, `${titulo}.`), pessoas.map((x) => blocoGanhos(x.colaborador, x.ganhos))],
      };
    },
    botoes: [{
      texto: 'Baixar o demonstrativo em PDF',
      gerar: async (r) => ({
        blob: await ganhosEmPdf({ pessoas: r.pessoas, empresa: sessao.empresa }),
        nome: nomeDoArquivoDosGanhos({ de: r.de, ate: r.ate, colaborador: r.pessoa === 'todas' ? null : r.pessoas[0]?.colaborador }),
        titulo: 'Demonstrativo de remuneração',
      }),
    }],
  });
}

// ---------------------------------------------------------------------
// Para o contador
// ---------------------------------------------------------------------
// Os lançamentos do relatório do contador, para conferir (0.23.2).
const juntarTextos = (...partes) => partes.filter(Boolean).join(' · ');
const itensDasCargas = (lista) => lista.map((c) => ({
  data: c.data, nome: c.cliente || 'Carga', detalhe: juntarTextos(c.rota, c.cte ? `CT-e ${c.cte}` : null), valor: c.bruto,
}));
const itensDasDespesas = (lista) => lista.map((x) => ({
  data: x.data,
  nome: x.categoria,
  detalhe: juntarTextos(x.veiculo ? formatarPlaca(x.veiculo) : null, x.fornecedor, x.descricao, x.nota ? `nota ${x.nota}` : null, x.parcela ? `parcela ${x.parcela}` : null),
  valor: x.valor,
}));

function blocoContador(r, fotos) {
  const t = r.totaisCargas;
  const caixa = r.regime === 'caixa';
  return [
    secao('Fretes', h('div', { class: 'grupo-valores' },
      linhaComItens(`${caixa ? 'Quitados' : 'Carregados'} no período (${plural(t.quantidade, 'carga')}): valor bruto`, t.bruto, itensDasCargas(r.cargas)),
      t.pedagio !== 0 && linha('Pedágio embutido no frete', t.pedagio),
      t.estadia !== 0 && linha('Estadias', t.estadia),
      linha('INSS retido', t.inss),
      linha('SEST/SENAT retido', t.sest),
      linha('IRRF retido', t.irrf),
      t.outros !== 0 && linha('Outros descontos do contratante', t.outros),
      linha('= Líquido (com as estadias)', t.liquido, { classe: 'total' }),
      caixa && linha('Recebido no período', r.totalRecebido, { dica: `${plural(r.recebimentos.length, 'recebimento')} (adiantamentos e saldos)` }))),
    secao('Despesas por categoria', h('div', { class: 'grupo-valores' },
      r.porCategoria.map((c) => linhaComItens(c.categoria, c.total, itensDasDespesas(r.despesas.filter((x) => x.categoria === c.categoria)))),
      r.porCategoria.length === 0 && h('p', { class: 'pequeno' }, 'Nenhuma despesa no período.'),
      linha('= Despesas', r.totalDespesas, { classe: 'total' }))),
    secao(caixa ? 'Pago aos colaboradores' : 'Remuneração dos colaboradores', h('div', { class: 'grupo-valores' },
      r.remuneracao.map((x) => linha(nomeDaPessoa(x.colaborador), x.valor, {
        dica: caixa && x.reembolso ? `${dinheiro(x.pago)} em vales e acertos, menos ${dinheiro(x.reembolso)} de reembolso das despesas dele` : null,
      })),
      r.remuneracao.length === 0 && h('p', { class: 'pequeno' }, 'Nenhuma no período.'),
      linha('= Total', r.totalRemuneracao, { classe: 'total' }))),
    caixa && r.historico && h('p', { class: 'pequeno' }, 'Parte do período veio dos programas antigos, que não registravam os pagamentos das comissões: no "Pago aos colaboradores" aparecem só os vales.'),
    r.agenciamentos.length > 0 && secao('Agenciamentos de transportadoras', h('div', { class: 'grupo-valores' },
      linhaComItens(`${plural(r.agenciamentos.length, 'carga')}`, r.totalAgenciamentos, r.agenciamentos.map((a) => ({
        data: a.data, nome: a.cliente || 'Carga', detalhe: a.pago ? null : 'a pagar', valor: a.valor,
      }))))),
    h('p', { class: 'pequeno' }, fotos > 0
      ? `${plural(fotos, 'foto', 'fotos')} de notas e documentos das cargas no período: vão no .zip, junto com o resumo em PDF.`
      : 'Nenhuma foto de nota ou documento no período.'),
  ];
}

export function telaRelatorioContador({ busca }) {
  return telaDeRelatorio({
    rota: '/relatorios/contador',
    titulo: 'Para o contador',
    busca,
    padrao: 'mes-passado',
    explicacao: 'Fretes com o INSS, o SEST/SENAT e o IRRF retidos, despesas por categoria e o que foi pago aos colaboradores. '
      + 'O .zip leva o resumo em PDF e as fotos das notas (as que não estão no aparelho precisam de internet).',
    extras: {
      inicial: (b) => {
        const pedido = b.get('regime');
        return { regime: Object.hasOwn(REGIMES, pedido ?? '') ? pedido : sessao.empresa?.relatorio_contabil === 'caixa' ? 'caixa' : 'competencia' };
      },
      campos: (filtro, dados, mudou) => campo({
        rotulo: 'Regime',
        nome: 'regime',
        tipo: 'segmentos',
        valor: filtro.regime,
        opcoes: [['competencia', 'Competência', 'data da carga e da despesa'], ['caixa', 'Caixa', 'data em que o dinheiro entrou ou saiu']],
        dica: `O padrão da empresa é ${sessao.empresa?.relatorio_contabil === 'caixa' ? 'caixa' : 'competência'} (Mais → Conta e empresa).`,
        aoMudar: mudou,
      }),
      ler: (form) => ({ regime: form.querySelector('[name="regime"]:checked')?.value ?? 'competencia' }),
      endereco: (filtro) => ({ regime: filtro.regime }),
    },
    montar: (dados, filtro, { de, ate, hoje }) => {
      const r = relatorioDoContador(dados, { de: `${de}-01`, ate: ultimoDiaDoMes(ate), hoje, regime: filtro.regime }, contas);
      const fotos = anexosDoRelatorio(r, dados.anexos);
      const vazio = r.cargas.length === 0 && r.despesas.length === 0 && r.recebimentos.length === 0 && r.remuneracao.length === 0;
      return {
        resultado: { relatorio: r, fotos },
        vazio,
        conteudo: vazio
          ? faixa(`Nenhuma carga, despesa ou pagamento em ${tituloDoPeriodo(de, ate)}.`, 'info')
          : [h('p', { class: 'pequeno' }, `${tituloDoPeriodo(de, ate)} · ${REGIMES[r.regime]}.`), blocoContador(r, fotos.length)],
      };
    },
    botoes: [
      {
        texto: 'Baixar o resumo em PDF',
        gerar: async ({ relatorio: r }) => ({
          blob: await contadorEmPdf({ relatorio: r, empresa: sessao.empresa }),
          nome: nomeDoArquivoDoContador(r),
          titulo: 'Relatório para o contador',
        }),
      },
      {
        texto: 'Baixar o .zip com as fotos das notas',
        gerar: async ({ relatorio: r, fotos }, filtro, progresso) => {
          const pdf = await contadorEmPdf({ relatorio: r, empresa: sessao.empresa });
          const nomes = nomesNoPacote(fotos);
          const arquivos = [{ nome: nomeDoArquivoDoContador(r), dados: new Uint8Array(await pdf.arrayBuffer()) }];
          const faltando = [];
          for (let i = 0; i < fotos.length; i++) {
            progresso(`Baixando as fotos (${i + 1} de ${fotos.length})…`);
            const blob = await anexosDados.baixar(fotos[i].anexo).catch(() => null);
            if (blob) arquivos.push({ nome: nomes[i], dados: new Uint8Array(await blob.arrayBuffer()) });
            else faltando.push(nomes[i]);
          }
          if (faltando.length) {
            arquivos.push({
              nome: 'FALTANDO.txt',
              dados: new TextEncoder().encode(`Estas fotos não puderam ser baixadas (sem internet?). Gere o .zip de novo com sinal:\r\n\r\n${faltando.join('\r\n')}\r\n`),
            });
          }
          return {
            blob: montarZip(arquivos),
            nome: nomeDoZipDoContador(r),
            titulo: 'Relatório para o contador',
            aviso: faltando.length ? `${plural(faltando.length, 'foto ficou', 'fotos ficaram')} de fora (sem internet). A lista está no arquivo FALTANDO.txt.` : null,
            avisoTipo: 'erro',
          };
        },
      },
    ],
  });
}
